import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { Router } from "@angular/router";

import { AuthService } from "src/app/auth/auth.service";
import { NotificationService } from "src/app/shared/services/notification.service";
import { DueProcessPermissions } from "src/app/shared/permissions/due-process-permissions";
import { FacilitationPermissions } from "src/app/shared/permissions/facilitation-permissions";
import { MediationPermissions } from "src/app/shared/permissions/mediation-permissions";
import { StateComplaintPermissions } from "src/app/shared/permissions/state-complaint-permissions";
import { DisputeCaseType } from "../../../models/dispute-resolution/dispute-base-models";
import { LinkedCaseDto } from "../../../models/dispute-resolution/linked-case-models";
import { LinkedCasesService } from "../../../services/dispute-resolution/linked-cases.service";

@Component({
  selector: "app-linked-cases",
  templateUrl: "./linked-cases.component.html",
  styleUrls: ["./linked-cases.component.scss"],
  standalone: false,
})
export class LinkedCasesComponent implements OnInit, OnChanges {
  @Input() caseType!: DisputeCaseType;
  @Input() caseId: string | undefined;
  @Input() clickable = false;
  /**
   * Show the Link Case button inside this section's card. Admin views set this
   * true (after their own Edit-permission check via AuthService); other variants
   * leave it false.
   */
  @Input() showLinkButton = false;
  /**
   * Show the unlink trash-can icon in an Actions column on each row. Admin views
   * set this true (using the same Edit-permission check that gates showLinkButton).
   */
  @Input() editable = false;
  /**
   * Route segment used when constructing the linked case's detail URL —
   * `admin`, `cde`, `contractor`, or `public`. Each per-case-type routing
   * module defines these as routes (e.g. `/dispute-resolution/mediation/admin/:id`).
   * The component inherits the caller's variant so clicking a linked case
   * lands on the matching view variant. (Note: `cde/:id` currently aliases
   * to the same component as `admin/:id` on the case types built so far;
   * keeping it in the union in case it gets a distinct surface later.)
   */
  @Input() linkedViewSegment: "admin" | "cde" | "contractor" | "public" = "admin";

  linkedCases: LinkedCaseDto[] = [];
  loading = false;
  readonly DisputeCaseType = DisputeCaseType;
  private readonly baseColumns: string[] = ["caseNumber", "type", "requester", "student", "status"];

  get displayedColumns(): string[] {
    return this.editable ? [...this.baseColumns, "actions"] : this.baseColumns;
  }

  constructor(
    private linkedCasesService: LinkedCasesService,
    private authService: AuthService,
    private router: Router,
    private notification: NotificationService
  ) {}

  onUnlinkClick(linked: LinkedCaseDto): void {
    if (!this.editable || this.caseType == null || !this.caseId) return;
    this.notification.confirmation(
      `Remove the link to case ${linked.caseNumber}? This will remove the link from both cases.`,
      () => {
        this.linkedCasesService.deleteLink(
          this.caseType,
          this.caseId!,
          linked.caseType,
          linked.caseId
        ).subscribe({
          next: () => {
            this.linkedCases = this.linkedCases.filter(c => c.caseId !== linked.caseId);
            this.notification.success(`Removed link to case ${linked.caseNumber}.`);
          },
          error: () => {
            this.notification.error("Failed to remove the link. Please try again.");
          },
        });
      }
    );
  }

  onLinkCaseClick(): void {
    if (!this.showLinkButton || this.caseType == null || !this.caseId) return;
    this.router.navigate([
      "/dispute-resolution",
      "linked-cases",
      DisputeCaseType[this.caseType],
      this.caseId,
    ]);
  }

  /**
   * True when the current user's role has access to view cases of the linked
   * type. Used by the template to decide whether to render Case ID as a
   * navigable anchor or as plain text. Mediation and IEP Facilitation also
   * accept ViewSummary so contractors — who hold ViewSummary but not View —
   * can still click through to their contractor view. (Due Process and State
   * Complaint have no contractor surface, so View alone is the right gate.)
   */
  canViewCaseType(type: DisputeCaseType): boolean {
    switch (type) {
      case DisputeCaseType.DueProcess:
        return this.authService.getPermission(DueProcessPermissions.View);
      case DisputeCaseType.Mediation:
        return this.authService.getPermission(MediationPermissions.View)
          || this.authService.getPermission(MediationPermissions.ViewSummary);
      case DisputeCaseType.IEPFacilitation:
        return this.authService.getPermission(FacilitationPermissions.View)
          || this.authService.getPermission(FacilitationPermissions.ViewSummary);
      case DisputeCaseType.StateComplaint:
        return this.authService.getPermission(StateComplaintPermissions.View);
      default:
        return false;
    }
  }

  ngOnInit(): void {
    this.loadLinkedCases();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["caseId"] && !changes["caseId"].firstChange) {
      this.loadLinkedCases();
    }
  }

  caseTypeLabel(type: DisputeCaseType): string {
    switch (type) {
      case DisputeCaseType.DueProcess:
        return "Due Process";
      case DisputeCaseType.Mediation:
        return "Mediation";
      case DisputeCaseType.IEPFacilitation:
        return "IEP Facilitation";
      case DisputeCaseType.StateComplaint:
        return "State Complaint";
      case DisputeCaseType.AreaOfConcern:
        return "Area of Concern";
      case DisputeCaseType.ParentEngagement:
        return "Parent Engagement";
      case DisputeCaseType.EarlyDisputeResolution:
        return "Early Dispute Resolution";
      default:
        return "";
    }
  }

  linkedCaseRoute(linked: LinkedCaseDto): string[] {
    const typeSegment = this.routeSegmentForType(linked.caseType);
    if (!typeSegment) return [];
    return [
      "/dispute-resolution",
      typeSegment,
      this.linkedViewSegment,
      linked.caseId,
    ];
  }

  private routeSegmentForType(type: DisputeCaseType): string {
    switch (type) {
      case DisputeCaseType.DueProcess:
        return "due-process";
      case DisputeCaseType.Mediation:
        return "mediation";
      case DisputeCaseType.IEPFacilitation:
        return "iep-facilitation";
      case DisputeCaseType.StateComplaint:
        return "state-complaint";
      default:
        return "";
    }
  }

  private loadLinkedCases(): void {
    if (this.caseType == null || !this.caseId) {
      this.linkedCases = [];
      return;
    }
    this.loading = true;
    this.linkedCasesService.getLinkedCases(this.caseType, this.caseId).subscribe({
      next: (result) => {
        this.linkedCases = result ?? [];
        this.loading = false;
      },
      error: () => {
        this.linkedCases = [];
        this.loading = false;
      },
    });
  }
}
